import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { berlinDate, legalBreakMinutes, shiftHours, SHIFT_SCOPES, SHIFT_SCOPE_LABELS, SKIP_REASON_LABELS, toMinutes, fromMinutes, type GenerateShiftsResult, type Shift, type ShiftEmployee, type ShiftScope, type ShiftSettings } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { useSession } from '../../session';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { dateDe, dayLabel, hoursDe, movePeriod, periodOf, SHIFT_VIEW_LABELS, type ShiftView } from './format';
import { ShiftBoard } from './ShiftBoard';
import { ShiftDayGrid } from './ShiftDayGrid';
import { DeleteShiftsDialog, ShiftMenu, type ShiftMenuActions } from './ShiftMenu';
import { GridStepSelect, useGridStep } from './gridStep';
import './shifts.css';

/**
 * Schichtplanung (KON-040): Plan als Kalenderraster, Entwurf per Formular erstellen, einzelne Schichten sofort wirksam anlegen. Ein Klick wählt eine Schicht, „Auswählen“ mehrere,
 * ein Rechtsklick öffnet das Menü mit Aufgaben, Fahrzeug, Auftrag und Löschen (Änderungswunsch vom 30.10.2026).
 * Wer das Recht shifts.write hat, verwaltet alle Pläne. Wer nur lesen darf, sieht hier ausschließlich den eigenen veröffentlichten Plan, das erzwingt der Server.
 * Wochenstunden und Urlaubsanspruch liest der Server aus der Mitarbeitermaske, hier werden sie nur angezeigt.
 */
export function ShiftsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const manage = can('shifts.write');
  const canDelete = can('shifts.delete');

  const [view, setView] = useState<ShiftView>('week');
  const [step, setStep] = useGridStep();
  const [anchor, setAnchor] = useState(berlinDate());
  const [employeeId, setEmployeeId] = useState('');
  const [filter, setFilter] = useState({ q: '', department: '', position: '', status: '' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Ein Klick wählt genau eine Schicht. Erst mit „Auswählen“ sammelt jeder Klick weitere Schichten (Mehrfachauswahl).
  const [multi, setMulti] = useState(false);
  const [menu, setMenu] = useState<{ shift: Shift; x: number; y: number } | null>(null);
  // Schichten, deren Löschen auf die Bestätigung im Modalfenster wartet
  const [confirmDelete, setConfirmDelete] = useState<Shift[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Vorbelegung für „Einzelne Schicht“, zum Beispiel aus der Liste der Tage ohne Schicht
  const [single, setSingle] = useState<{ employeeId: string; date: string } | null>(null);
  const period = periodOf(view, anchor);

  const employees = useQuery({ queryKey: ['shift-employees'], queryFn: async () => (await api<{ data: ShiftEmployee[] }>('GET', '/shifts/employees')).data, enabled: manage });
  const shifts = useQuery({
    queryKey: ['shifts', period.from, period.to, manage ? employeeId : 'own'],
    queryFn: async () => (await api<{ data: Shift[] }>('GET', `/shifts${queryString({ from: period.from, to: period.to, employeeId: manage ? employeeId || undefined : undefined })}`)).data,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['shifts'] });

  // Eine Auswahl gilt nur für den Zeitraum und den Mitarbeiter, in dem sie getroffen wurde
  useEffect(() => {
    setSelected(new Set());
    setEditing(false);
  }, [period.from, period.to, employeeId, filter]);

  // Suche und Filter wirken auf die Mitarbeiter (Name, Personalnummer, Position, Abteilung) und den Stand der Schichten
  const people = employees.data ?? [];
  const wanted = fold(filter.q).split(/\s+/).filter(Boolean);
  const matching = new Set(
    people
      .filter((e) => (!filter.department || e.department === filter.department) && (!filter.position || e.position === filter.position))
      .filter((e) => wanted.every((w) => fold([e.name, e.personnelNumber, e.position ?? '', e.department ?? ''].join(' ')).includes(w)))
      .map((e) => e.id),
  );
  const filtering = manage && (!!filter.q || !!filter.department || !!filter.position);
  const list = (shifts.data ?? []).filter((s) => (!filtering || matching.has(s.employeeId)) && (!filter.status || s.status === filter.status));
  const fields: FilterField[] = [
    { key: 'department', label: 'Abteilung', type: 'select', options: [{ value: '', label: 'Alle Abteilungen' }, ...[...new Set(people.map((e) => e.department).filter((d): d is string => !!d))].sort().map((d) => ({ value: d, label: d }))] },
    { key: 'position', label: 'Position', type: 'select', options: [{ value: '', label: 'Alle Positionen' }, ...[...new Set(people.map((e) => e.position).filter((d): d is string => !!d))].sort().map((d) => ({ value: d, label: d }))] },
    { key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'draft', label: 'Entwurf' }, { value: 'published', label: 'Veröffentlicht' }] },
  ];
  const pick = list.filter((s) => selected.has(s.id));
  const drafts = pick.filter((s) => s.status === 'draft').length;
  const published = pick.length - drafts;
  const total = list.reduce((sum, s) => sum + s.hours, 0);
  const showNames = manage && new Set(list.map((s) => s.employeeId)).size > 1;

  const bulk = useMutation({
    mutationFn: ({ action, ids }: { action: 'publish' | 'delete'; ids: string[] }) => api<{ data: { changed: number; skipped: number } }>('POST', '/shifts/bulk', { ids, action }),
    onSuccess: (res, { action }) => {
      const verb = action === 'publish' ? 'veröffentlicht' : 'gelöscht';
      setNotice(`${res.data.changed} ${res.data.changed === 1 ? 'Schicht' : 'Schichten'} ${verb}${res.data.skipped > 0 ? `, ${res.data.skipped} übersprungen (falscher Stand)` : ''}.`);
      setSelected(new Set());
      setConfirmDelete(null);
      setEditing(false);
      refresh();
    },
    onError: () => setNotice(null),
  });

  /** Klick auf eine Schicht: einzeln wählt er genau diese (ein zweiter Klick hebt die Wahl auf), in der Mehrfachauswahl kommt sie dazu oder fällt heraus */
  const toggle = (id: string) =>
    setSelected((s) => {
      if (!multi) return s.size === 1 && s.has(id) ? new Set() : new Set([id]);
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  // Kopf eines Tages oder einer Person wählt mehrere Schichten, damit beginnt die Mehrfachauswahl
  const toggleIds = (ids: string[]) => {
    setMulti(true);
    setSelected((s) => {
      const all = ids.every((id) => s.has(id));
      const next = new Set(s);
      for (const id of ids) {
        if (all) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };
  const toggleDay = (day: string) => toggleIds(list.filter((x) => x.date === day).map((x) => x.id));
  const endMulti = () => {
    setMulti(false);
    setSelected(new Set());
  };
  const error = [shifts.error, confirmDelete ? null : bulk.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  // Bearbeiten geht für eine einzelne Schicht, auch eine veröffentlichte
  const selectedOne = pick.length === 1 ? pick[0]! : null;
  const openMenu = manage ? (shift: Shift, x: number, y: number) => setMenu({ shift, x, y }) : undefined;
  const menuActions: ShiftMenuActions = {
    edit: (shift) => {
      setMulti(false);
      setSelected(new Set([shift.id]));
      setEditing(true);
    },
    publish: (shift) => bulk.mutate({ action: 'publish', ids: [shift.id] }),
    remove: (list) => {
      bulk.reset();
      setConfirmDelete(list);
    },
    done: (text) => {
      setNotice(text);
      refresh();
    },
  };

  return (
    <>
      <div className="page-head">
        <h1>Schichtplanung</h1>
        <span className="muted">{list.length > 0 ? `${list.length} Schichten, ${hoursDe(total)}` : ''}</span>
      </div>

      {manage && (
        <div className="shift-actions">
          <GenerateForm employees={employees.data ?? []} defaultEmployee={employeeId} anchor={anchor} onDone={(from) => { setAnchor(from); refresh(); }} onPlanSingle={(employee, date) => setSingle({ employeeId: employee, date })} />
          <SingleShiftForm employees={employees.data ?? []} defaultEmployee={employeeId} anchor={anchor} prefill={single} onDone={(date) => { setAnchor(date); setSingle(null); refresh(); }} onClose={() => setSingle(null)} />
        </div>
      )}

      <section className="card">
        {manage && (
          <FilterBar
            search={{ value: filter.q, placeholder: 'Name, Personalnummer, Position, Abteilung', onSubmit: (v) => setFilter((f) => ({ ...f, q: v })) }}
            fields={fields}
            values={{ department: filter.department, position: filter.position, status: filter.status }}
            onChange={(key, value) => setFilter((f) => ({ ...f, [key]: value }))}
            onReset={() => setFilter({ q: '', department: '', position: '', status: '' })}
          />
        )}
        <div className="shift-toolbar">
          <div className="shift-toolbar__nav" role="group" aria-label="Zeitraum">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(movePeriod(view, anchor, -1))} aria-label="Zurück">←</button>
            <strong className="shift-toolbar__label">{period.label}</strong>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(movePeriod(view, anchor, 1))} aria-label="Weiter">→</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(berlinDate())}>Heute</button>
          </div>
          <label className="field shift-toolbar__field">
            <span>Ansicht</span>
            <select className="input" value={view} onChange={(e) => setView(e.target.value as ShiftView)}>
              {(['day', 'week', 'month'] as ShiftView[]).map((v) => <option key={v} value={v}>{SHIFT_VIEW_LABELS[v]}</option>)}
            </select>
          </label>
          {view === 'day' && <GridStepSelect value={step} onChange={setStep} />}
          {manage && (
            <label className="field shift-toolbar__field">
              <span>Mitarbeiter</span>
              <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">Alle</option>
                {(employees.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </label>
          )}
        </div>

        {manage && filtering && <p className="muted small">{matching.size} {matching.size === 1 ? 'Mitarbeiter' : 'Mitarbeiter'} passen zur Suche.</p>}
        {!manage && <p className="muted small">Sie sehen hier Ihren eigenen, veröffentlichten Schichtplan. Pläne anderer Mitarbeiter sehen nur Verwalter der Schichtplanung.</p>}
        {error && <div className="alert alert--error" role="alert">{error.message}</div>}
        {notice && <div className="alert alert--ok" role="status">{notice}</div>}

        {manage && list.length > 0 && (
          <div className="shift-select" role="toolbar" aria-label="Auswahl">
            <span className="muted small" aria-live="polite">
              {pick.length === 0
                ? multi
                  ? 'Mehrfachauswahl: Schichten, Tages- oder Personenköpfe anklicken.'
                  : 'Schicht anklicken, um sie auszuwählen. Rechtsklick öffnet weitere Aktionen.'
                : `${pick.length} ausgewählt${pick.length > 1 || drafts > 0 ? ` (${drafts} Entwurf, ${published} veröffentlicht)` : ''}`}
            </span>
            <span className="row-actions">
              <button type="button" className={`btn btn--sm ${multi ? 'btn--primary' : 'btn--secondary'}`} aria-pressed={multi} onClick={() => (multi ? endMulti() : setMulti(true))}>{multi ? 'Auswahl beenden' : 'Auswählen'}</button>
              {multi && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSelected(pick.length === list.length ? new Set() : new Set(list.map((s) => s.id)))}>{pick.length === list.length ? 'Auswahl aufheben' : 'Alle auswählen'}</button>}
              {pick.length > 0 && (
                <>
                  {selectedOne && <button type="button" className="btn btn--secondary btn--sm" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>Bearbeiten</button>}
                  {drafts > 0 && <button type="button" className="btn btn--primary btn--sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: 'publish', ids: pick.filter((s) => s.status === 'draft').map((s) => s.id) })}>Veröffentlichen ({drafts})</button>}
                  {canDelete && (
                    <button type="button" className="btn btn--danger btn--sm" disabled={bulk.isPending} onClick={() => menuActions.remove(pick)}>
                      Löschen ({pick.length})
                    </button>
                  )}
                </>
              )}
            </span>
          </div>
        )}
        {confirmDelete && (
          <DeleteShiftsDialog
            shifts={confirmDelete}
            busy={bulk.isPending}
            error={bulk.error instanceof ApiError ? bulk.error.message : bulk.error ? 'Die Schichten konnten nicht gelöscht werden.' : null}
            onConfirm={() => bulk.mutate({ action: 'delete', ids: confirmDelete.map((s) => s.id) })}
            onClose={() => setConfirmDelete(null)}
          />
        )}
        {menu && (
          <ShiftMenu
            shift={menu.shift}
            x={menu.x}
            y={menu.y}
            employee={employees.data?.find((e) => e.id === menu.shift.employeeId)}
            actions={menuActions}
            onClose={() => setMenu(null)}
          />
        )}
        {editing && selectedOne && <ShiftEditForm shift={selectedOne} onDone={() => { setEditing(false); setSelected(new Set()); refresh(); }} onCancel={() => setEditing(false)} />}

        {shifts.isLoading ? (
          <p className="muted">Wird geladen …</p>
        ) : (
          view === 'day' ? (
            <ShiftDayGrid day={anchor} step={step} shifts={list} selected={manage ? selected : undefined} onToggle={manage ? toggle : undefined} onToggleIds={manage ? toggleIds : undefined} positions={employees.data ? new Map(employees.data.map((e) => [e.id, e.position])) : undefined} linkEmployees={can('hr.read')} onMenu={openMenu} />
          ) : (
            <ShiftBoard days={period.days} month={view === 'month'} shifts={list} showNames={showNames} selected={manage ? selected : undefined} onToggle={manage ? toggle : undefined} onToggleDay={manage ? toggleDay : undefined} onMenu={openMenu} />
          )
        )}
        {!shifts.isLoading && list.length === 0 && <p className="empty">{manage ? 'Keine Schichten im Zeitraum. Mit „Entwurf erstellen“ legen Sie einen Plan an.' : 'Keine veröffentlichten Schichten im Zeitraum.'}</p>}

        <div className="shift-legend muted small" aria-hidden="true">
          <span><i className="shift-legend__swatch shift-legend__swatch--draft" /> Entwurf (gestrichelt, schraffiert)</span>
          <span><i className="shift-legend__swatch shift-legend__swatch--published" /> Veröffentlicht (ausgefüllt)</span>
        </div>
      </section>
    </>
  );
}

/** Klein geschrieben und ohne Akzente, damit „muller“ auch „Müller“ findet */
const fold = (text: string) => text.toLocaleLowerCase('de-DE').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

/** Formular „Entwurf erstellen“: Mitarbeiter wählen, Zeitraum, Beginn. Stunden und Urlaubstage kommen aus der Mitarbeitermaske. */
function GenerateForm({ employees, defaultEmployee, anchor, onDone, onPlanSingle }: { employees: ShiftEmployee[]; defaultEmployee: string; anchor: string; onDone: (from: string) => void; onPlanSingle: (employeeId: string, date: string) => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [scope, setScope] = useState<ShiftScope>('week');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<GenerateShiftsResult | null>(null);

  const chosen = employees.find((e) => e.id === (employeeId || defaultEmployee));
  const effectiveDate = date || anchor;

  const generate = useMutation({
    mutationFn: () => api<{ data: GenerateShiftsResult }>('POST', '/shifts/generate', { employeeId: chosen?.id ?? '', scope, date: effectiveDate, ...(startTime ? { startTime } : {}) }),
    onSuccess: (res) => {
      setErrors({});
      setResult(res.data);
      onDone(res.data.from);
    },
    onError: (e) => {
      setResult(null);
      setErrors(e instanceof ApiError ? (e.status === 422 ? e.fields : { employeeId: e.message }) : { employeeId: 'Der Entwurf konnte nicht erstellt werden.' });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    generate.mutate();
  }

  // Zuerst steht nur ein Knopf, die Maske öffnet sich erst nach dem Anklicken (am Handy und am Desktop)
  const [open, setOpen] = useState(false);
  if (!open && !result) {
    return (
      <div className="shift-generate-toggle">
        <button type="button" className="btn btn--primary" aria-expanded={false} aria-controls="shift-generate" onClick={() => setOpen(true)}>+ Entwurf erstellen</button>
      </div>
    );
  }

  return (
    <section className="card" id="shift-generate">
      <div className="shift-generate-head">
        <h2>Entwurf erstellen</h2>
        <button type="button" className="btn btn--secondary btn--sm" aria-expanded onClick={() => { setOpen(false); setResult(null); }}>Schließen</button>
      </div>
      <p className="muted small">Aus den Wochenstunden des Mitarbeiters, den Arbeitstagen der Einrichtung, den Feiertagen und genehmigten Freistellungen entsteht ein Entwurf. Vorhandene Entwürfe im Zeitraum werden ersetzt, veröffentlichte Schichten bleiben. Feiertage bekommen keine Schicht. Wer an einem Feiertag arbeitet, wird über „Einzelne Schicht“ eingeplant.</p>
      <form className="shift-form" onSubmit={submit} noValidate>
        <label className="field">
          <span>Mitarbeiter</span>
          <select className="input" value={chosen?.id ?? ''} onChange={(e) => setEmployeeId(e.target.value)} aria-invalid={!!errors.employeeId}>
            <option value="">Bitte wählen</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` (${e.position})` : ''}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Zeitraum</span>
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value as ShiftScope)}>
            {SHIFT_SCOPES.map((s) => <option key={s} value={s}>{SHIFT_SCOPE_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{scope === 'rest_of_year' ? 'Ab Datum' : scope === 'week' ? 'Eine Woche am' : 'Ein Monat am'}</span>
          <input className="input" type="date" value={effectiveDate} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.date} />
          {errors.date && <small className="field__error" role="alert">{errors.date}</small>}
        </label>
        <label className="field">
          <span>Beginn (optional)</span>
          <input className="input" type="time" step={900} value={startTime} onChange={(e) => setStartTime(e.target.value)} aria-invalid={!!errors.startTime} />
          {errors.startTime && <small className="field__error" role="alert">{errors.startTime}</small>}
        </label>
        <button className="btn btn--primary shift-form__submit" type="submit" disabled={generate.isPending || !chosen}>{generate.isPending ? 'Wird erstellt …' : 'Entwurf erstellen'}</button>
      </form>
      {errors.employeeId && <div className="alert alert--error" role="alert">{errors.employeeId}</div>}

      {chosen && (
        <p className="muted small shift-facts">
          {chosen.name}: {chosen.weeklyHours === null ? 'keine Wochenstunden hinterlegt' : `${chosen.weeklyHours.toLocaleString('de-DE')} Wochenstunden (${chosen.weeklyHoursSource === 'employee' ? 'aus den Mitarbeiterdaten' : 'Vorgabe der Position'})`}
          {chosen.vacationDaysPerYear !== null ? `, ${chosen.vacationDaysPerYear.toLocaleString('de-DE')} Urlaubstage im Jahr` : ''}.
        </p>
      )}

      {result && (
        <div className="alert alert--ok" role="status">
          <strong>{result.created} Schichten als Entwurf erstellt</strong> ({hoursDe(result.plannedHours)}, {dateDe(result.from)} bis {dateDe(result.to)}).
          {result.replaced > 0 && <> {result.replaced} frühere Entwürfe ersetzt.</>}
          {result.vacation.entitlement !== null && <> Urlaub {result.vacation.year}: {result.vacation.remaining?.toLocaleString('de-DE')} von {result.vacation.entitlement.toLocaleString('de-DE')} Tagen übrig.</>}
          {result.skipped.length > 0 && (
            <details>
              <summary>{result.skipped.length} Tage ohne Schicht</summary>
              <ul>{result.skipped.map((s) => (
                <li key={s.date}>
                  {dayLabel(s.date)}: {SKIP_REASON_LABELS[s.reason]}
                  {s.reason === 'holiday' && chosen && <> <button type="button" className="btn btn--ghost btn--sm" onClick={() => onPlanSingle(chosen.id, s.date)}>Trotzdem einplanen</button></>}
                </li>
              ))}</ul>
            </details>
          )}
          {result.warnings.length > 0 && <ul className="shift-warnings">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
        </div>
      )}
    </section>
  );
}

/**
 * Einzelne Schicht (Änderungswunsch vom 22.10.2026): Ein Mitarbeiter wird für einen bestimmten Tag eingeplant, auch an Feiertagen und an Tagen, die in der Einrichtung keine Arbeitstage sind. Der Entwurf
 * für einen Zeitraum überspringt solche Tage, im Chauffeur-Betrieb und in der Gastronomie wird aber oft gerade dann gearbeitet. Die Regeln der Arbeitszeit gelten weiter (Höchstarbeitszeit, Ruhezeit,
 * Freistellungen), das prüft der Server. Die Schicht ist sofort wirksam, also veröffentlicht (Änderungswunsch vom 30.10.2026), anders als „Entwurf erstellen“. Ein späterer Entwurf für den
 * Zeitraum lässt sie stehen, weil er veröffentlichte Schichten nie ersetzt.
 */
function SingleShiftForm({ employees, defaultEmployee, anchor, prefill, onDone, onClose }: { employees: ShiftEmployee[]; defaultEmployee: string; anchor: string; prefill: { employeeId: string; date: string } | null; onDone: (date: string) => void; onClose: () => void }) {
  const settings = useQuery({ queryKey: ['shift-settings'], queryFn: async () => (await api<{ data: ShiftSettings }>('GET', '/shifts/settings')).data });
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [pause, setPause] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);

  // Aus der Liste der Tage ohne Schicht geöffnet: Mitarbeiter und Tag sind schon eingetragen
  useEffect(() => {
    if (!prefill) return;
    setOpen(true);
    setEmployeeId(prefill.employeeId);
    setDate(prefill.date);
    setDone(null);
  }, [prefill]);

  const chosen = employees.find((e) => e.id === (employeeId || defaultEmployee));
  const effectiveDate = date || anchor;
  // Vorgaben: Beginn aus der Einrichtung, Ende nach der Tagesarbeitszeit des Mitarbeiters (Wochenstunden durch Arbeitstage), mit der gesetzlichen Pause
  const startValue = start || settings.data?.defaultStart || '08:00';
  const workDays = settings.data?.workDays.length ?? 5;
  const dailyMinutes = Math.min(Math.round(((chosen?.weeklyHours ?? 40) / Math.max(workDays, 1)) * 60), (settings.data?.maxDailyHours ?? 10) * 60);
  const pauseValue = pause !== '' ? Number(pause) || 0 : legalBreakMinutes(dailyMinutes);
  const endValue = end || fromMinutes(Math.min(toMinutes(startValue) + dailyMinutes + pauseValue, 24 * 60 - 1));

  const save = useMutation({
    mutationFn: () => api<{ data: Shift }>('POST', '/shifts', { employeeId: chosen?.id ?? '', date: effectiveDate, startTime: startValue, endTime: endValue, breakMinutes: pauseValue, publish: true }),
    onSuccess: (res) => {
      setErrors({});
      setDone(`Schicht für ${res.data.employeeName} am ${dateDe(res.data.date)} angelegt (${hoursDe(res.data.hours)}). Sie gilt ab sofort und steht im Plan des Mitarbeiters.`);
      onDone(res.data.date);
    },
    onError: (e) => setErrors(e instanceof ApiError ? (e.status === 422 ? e.fields : { employeeId: e.message }) : { employeeId: 'Die Schicht konnte nicht angelegt werden.' }),
  });

  if (!open) {
    return (
      <div className="shift-generate-toggle">
        <button type="button" className="btn btn--secondary" aria-expanded={false} aria-controls="shift-single" onClick={() => setOpen(true)}>+ Einzelne Schicht</button>
        {done && <p className="muted small" role="status">{done}</p>}
      </div>
    );
  }

  return (
    <section className="card" id="shift-single">
      <div className="shift-generate-head">
        <h2>Einzelne Schicht</h2>
        <button type="button" className="btn btn--secondary btn--sm" aria-expanded onClick={() => { setOpen(false); onClose(); }}>Schließen</button>
      </div>
      <p className="muted small">Plant einen Mitarbeiter für genau einen Tag ein, auch an einem Feiertag oder an einem Tag, der in der Einrichtung kein Arbeitstag ist. Höchstarbeitszeit, Ruhezeit und Freistellungen gelten auch hier.</p>
      <form className="shift-form" onSubmit={(e) => { e.preventDefault(); setErrors({}); setDone(null); save.mutate(); }} noValidate>
        <label className="field">
          <span>Mitarbeiter</span>
          <select className="input" value={chosen?.id ?? ''} onChange={(e) => setEmployeeId(e.target.value)} aria-invalid={!!errors.employeeId}>
            <option value="">Bitte wählen</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` (${e.position})` : ''}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Tag</span>
          <input className="input" type="date" value={effectiveDate} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.date} />
          {errors.date && <small className="field__error" role="alert">{errors.date}</small>}
        </label>
        <label className="field">
          <span>Beginn</span>
          <input className="input" type="time" step={900} value={startValue} onChange={(e) => setStart(e.target.value)} aria-invalid={!!errors.startTime} />
          {errors.startTime && <small className="field__error" role="alert">{errors.startTime}</small>}
        </label>
        <label className="field">
          <span>Ende</span>
          <input className="input" type="time" step={900} value={endValue} onChange={(e) => setEnd(e.target.value)} aria-invalid={!!errors.endTime} />
          {errors.endTime && <small className="field__error" role="alert">{errors.endTime}</small>}
        </label>
        <label className="field">
          <span>Pause (Min.)</span>
          <input className="input" inputMode="numeric" value={pause !== '' ? pause : String(pauseValue)} onChange={(e) => setPause(e.target.value)} aria-invalid={!!errors.breakMinutes} />
          {errors.breakMinutes && <small className="field__error" role="alert">{errors.breakMinutes}</small>}
        </label>
        <button className="btn btn--primary shift-form__submit" type="submit" disabled={save.isPending || !chosen}>{save.isPending ? 'Wird angelegt …' : 'Schicht anlegen'}</button>
      </form>
      <p className="muted small">{shiftHours(startValue, endValue, pauseValue) > 0 ? `Arbeitszeit: ${hoursDe(shiftHours(startValue, endValue, pauseValue))}.` : ''}</p>
      {errors.employeeId && <div className="alert alert--error" role="alert">{errors.employeeId}</div>}
      {done && <div className="alert alert--ok" role="status">{done}</div>}
    </section>
  );
}

/** Zeiten einer ausgewählten Schicht ändern, auch einer veröffentlichten. Ruhezeit und Höchstarbeitszeit prüft der Server. */
function ShiftEditForm({ shift, onDone, onCancel }: { shift: Shift; onDone: () => void; onCancel: () => void }) {
  const [start, setStart] = useState(shift.startTime);
  const [end, setEnd] = useState(shift.endTime);
  const [pause, setPause] = useState(String(shift.breakMinutes));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => api('PATCH', `/shifts/${shift.id}`, { startTime: start, endTime: end, breakMinutes: Number(pause) || 0 }),
    onSuccess: onDone,
    onError: (e) => setErrors(e instanceof ApiError ? (e.status === 422 ? e.fields : { endTime: e.message }) : { endTime: 'Die Schicht konnte nicht gespeichert werden.' }),
  });
  return (
    <form className="shift-edit" onSubmit={(e) => { e.preventDefault(); save.mutate(); }} noValidate aria-label={`Schicht von ${shift.employeeName} am ${dateDe(shift.date)} bearbeiten`}>
      <strong>{shift.employeeName}, {dayLabel(shift.date)}</strong>
      <label className="field"><span>Beginn</span><input className="input" type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} aria-invalid={!!errors.startTime} /></label>
      <label className="field"><span>Ende</span><input className="input" type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} aria-invalid={!!errors.endTime} /></label>
      <label className="field"><span>Pause (Min.)</span><input className="input" inputMode="numeric" value={pause} onChange={(e) => setPause(e.target.value)} aria-invalid={!!errors.breakMinutes} /></label>
      {Object.values(errors)[0] && <small className="field__error" role="alert">{Object.values(errors)[0]}</small>}
      <span className="row-actions">
        <button className="btn btn--primary btn--sm" type="submit" disabled={save.isPending}>Speichern</button>
        <button className="btn btn--secondary btn--sm" type="button" onClick={onCancel}>Abbrechen</button>
      </span>
    </form>
  );
}
